// /src/components/ProtectedRoute.js
import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUserData } from '../auth/UserData';
import serverLogger from '../utils/ClientLogger';
import { useTranslation } from 'react-i18next';

const ACTIVE_MEMBERSHIP_STATUSES = new Set(['trialing', 'active']);
const DEFAULT_ALLOWED_PLANS = new Set(['basic', 'pro', 'elite']);

const normalisePlanKey = (value) => {
  if (!value) return null;
  return String(value).trim().toLowerCase();
};

const ProtectedRoute = ({
  children,
  requiredPermissions = [],
  requireMembership = false,
  allowedMembershipPlans = undefined,
  fallbackPath = '/unauthorized',
  fallbackState = undefined,
  membershipFallbackPath = '/pricing',
  membershipFallbackState = undefined,
}) => {
  const { t } = useTranslation();
  const { user, isLoading } = useUserData();
  const location = useLocation();

  // Permissions from DB roles — also include role slugs so callers can check e.g. 'flocktrader' or 'admin'
  const userPermissions = useMemo(() => {
    const permissions = new Set(Array.isArray(user?.permissions) ? user.permissions : []);
    (Array.isArray(user?.roleSlugs) ? user.roleSlugs : []).forEach((s) => permissions.add(s));
    return permissions;
  }, [user?.permissions, user?.roleSlugs]);

  if (isLoading) {
    // Vis en lasteskjerm mens brukerdata oppdateres
    return <div>{t('common.loading')}</div>;
  }

  if (!user) {
    serverLogger.log('info', 'User not authenticated:', user);
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const hasPermission =
    Array.isArray(requiredPermissions) && requiredPermissions.length > 0
      ? requiredPermissions.some((perm) => userPermissions.has(perm))
      : true;

  if (!hasPermission) {
    serverLogger.log('info', 'User lacks required permissions:', { userPermissions, requiredPermissions });
    return <Navigate to={fallbackPath} replace state={fallbackState ?? { from: location, reason: 'missing_permission' }} />;
  }

  const membership = user.membership || {};
  const membershipStatus = membership.status ? String(membership.status).toLowerCase() : 'inactive';
  const membershipPlanKey = normalisePlanKey(membership.planKey || membership.planNickname);

  const allowedPlans = allowedMembershipPlans
    ? new Set(allowedMembershipPlans.map((plan) => normalisePlanKey(plan)).filter(Boolean))
    : DEFAULT_ALLOWED_PLANS;

  const hasValidMembership =
    !requireMembership
      || (
        ACTIVE_MEMBERSHIP_STATUSES.has(membershipStatus)
        && (allowedPlans.size === 0 || (membershipPlanKey && allowedPlans.has(membershipPlanKey)))
      );

  if (!hasValidMembership) {
    serverLogger.log('info', 'User lacks required membership:', {
      membershipStatus,
      membershipPlanKey,
      allowedPlans: Array.from(allowedPlans),
      requireMembership,
    });
    return (
      <Navigate
        to={membershipFallbackPath}
        replace
        state={
          membershipFallbackState ?? {
            from: location,
            reason: 'membership_upgrade_required',
          }
        }
      />
    );
  }

  // Returner children hvis alt er validert
  return children;
};

export default ProtectedRoute;
